/** In-app tab history. Matches the prototype: one back/forward stack per tab, capped at 30. */

const HISTORY_LIMIT = 30;

export type WorkspaceTab = {
  id: string;
  pageId: string | null;
  back: string[];
  fwd: string[];
};

export function recordVisit(tab: WorkspaceTab, pageId: string | null): WorkspaceTab {
  if (tab.pageId === pageId) return tab;
  const back = tab.pageId === null ? tab.back : [...tab.back, tab.pageId].slice(-HISTORY_LIMIT);
  return { ...tab, pageId, back, fwd: [] };
}

/** Returns null when this tab has nothing to go back to. */
export function stepBack(tab: WorkspaceTab): WorkspaceTab | null {
  const to = tab.back[tab.back.length - 1];
  if (to === undefined) return null;
  const fwd = tab.pageId === null ? tab.fwd : [tab.pageId, ...tab.fwd];
  return { ...tab, pageId: to, back: tab.back.slice(0, -1), fwd };
}

/** Returns null when this tab has nothing to go forward to. */
export function stepForward(tab: WorkspaceTab): WorkspaceTab | null {
  const to = tab.fwd[0];
  if (to === undefined) return null;
  const back = tab.pageId === null ? tab.back : [...tab.back, tab.pageId].slice(-HISTORY_LIMIT);
  return { ...tab, pageId: to, back, fwd: tab.fwd.slice(1) };
}

/**
 * Close one tab. The last remaining tab stays open.
 * Closing the active tab activates the neighbour at the same index.
 */
export function closeTab(
  tabs: readonly WorkspaceTab[],
  activeId: string,
  closingId: string,
): { tabs: WorkspaceTab[]; activeId: string } | null {
  if (tabs.length < 2) return null;
  const index = tabs.findIndex((tab) => tab.id === closingId);
  if (index < 0) return null;
  const next = tabs.filter((tab) => tab.id !== closingId);
  if (activeId !== closingId) return { tabs: next, activeId };
  const neighbor = next[Math.min(index, next.length - 1)];
  if (!neighbor) return null;
  return { tabs: next, activeId: neighbor.id };
}

/**
 * Parent ids, root first, excluding the page itself. Stops on a cycle.
 * A database row points at its data source, so `parentPageId` (the page the
 * database sits on) wins over the raw `parentId` when present.
 */
export function ancestorIds(
  pages: readonly { id: string; parentId: string | null; parentPageId?: string | null }[],
  pageId: string,
): string[] {
  const byId = new Map(pages.map((page) => [page.id, page]));
  const chain: string[] = [];
  const seen = new Set<string>([pageId]);
  let parentId = byId.get(pageId)?.parentPageId ?? byId.get(pageId)?.parentId ?? null;
  while (parentId !== null && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    chain.unshift(parent.id);
    parentId = parent.parentPageId ?? parent.parentId;
  }
  return chain;
}
